import HighchartsPrivacyModeFilter from "components/Text/HighchartsPrivacyModeFilter";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import type React from "react";

// Always-on defaults (every chart): drop the Highcharts credits link and use a
// consistent no-data placeholder style.
const BASE_DEFAULTS: Highcharts.Options = {
  credits: { enabled: false },
  noData: { style: { fontWeight: "normal", fontSize: "15px", color: "black" } },
};

// Time-series-only defaults: the London timezone for datetime axes and the
// standard export menu. Categorical charts (pie / sankey / bar-by-category)
// opt out via `defaults="base"` so they don't silently gain a timezone axis or
// an export button they never had.
const TIMESERIES_DEFAULTS: Highcharts.Options = {
  time: { timezone: "Europe/London" },
  exporting: {
    fallbackToExportServer: false,
    buttons: {
      contextButton: {
        menuItems: [
          "viewFullscreen",
          "printChart",
          "separator",
          "downloadPNG",
          "downloadJPEG",
          "downloadSVG",
        ],
      },
    },
  },
};

type ChartDefaults = "timeseries" | "base";

/**
 * Merge the shared chart defaults under the caller's options:
 * `defaults < computed lang.noData < caller options` (last-wins, so any chart
 * can override them). `defaults="base"` applies only BASE_DEFAULTS (no timezone
 * / export menu). Pure + testable.
 */
export function buildChartOptions(
  options: Highcharts.Options,
  opts?: { loading?: boolean; noDataText?: string; defaults?: ChartDefaults },
): Highcharts.Options {
  const noData =
    opts?.noDataText ??
    (opts?.loading ? "Loading Data " : "No data for this time period");
  const base =
    (opts?.defaults ?? "timeseries") === "timeseries"
      ? Highcharts.merge(BASE_DEFAULTS, TIMESERIES_DEFAULTS)
      : BASE_DEFAULTS;
  return Highcharts.merge(base, { lang: { noData } }, options);
}

interface ChartProps
  extends Omit<
    React.ComponentProps<typeof HighchartsReact>,
    "highcharts" | "options"
  > {
  /** Per-chart config: series, axes, chart.type, tooltips, … */
  options: Highcharts.Options;
  /** Drives the default `lang.noData` ("Loading Data " vs "No data…"). */
  loading?: boolean;
  /** Override the loading/no-data placeholder text. */
  noDataText?: string;
  /** "timeseries" (default) layers the London timezone + export menu under the
   * caller; "base" applies only credits/no-data so categorical charts don't
   * gain a datetime axis or an export button. */
  defaults?: ChartDefaults;
  /** When true, render the blur <filter> def (callers apply `filter:url(#blur)`
   * in their own tooltips). Pass your computed privacyMode. */
  privacyMode?: boolean;
}

/**
 * Opt-in wrapper over HighchartsReact that applies the shared Highcharts option
 * defaults and (optionally) the privacy-mode blur filter, so chart components
 * only declare what's actually specific to them. Any extra HighchartsReact prop
 * (immutable, callback, allowChartUpdate, containerProps, …) passes straight
 * through.
 */
export default function Chart({
  options,
  loading,
  noDataText,
  defaults,
  privacyMode,
  ...rest
}: ChartProps): React.ReactElement {
  return (
    <>
      {privacyMode && <HighchartsPrivacyModeFilter />}
      <HighchartsReact
        highcharts={Highcharts}
        options={buildChartOptions(options, { loading, noDataText, defaults })}
        {...rest}
      />
    </>
  );
}
